import { Component, inject, OnInit, OnDestroy, signal, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { of, EMPTY, Subject, switchMap, take, takeUntil, filter, map, timeout } from 'rxjs';
import { settledCache$ } from '../../shared/utils/cache-ready';

import { ContractService } from '../../service/entity-services/contract.service';
import { UsersService } from '../../service/entity-services/users.service';
import { DocumentAnalysisService, DocumentAnalysisResponse, AnalysisProgress, describeAnalysisFailure } from '../../service/ai/document-analysis.service';
import { Contract } from '../../objects/contract';
import { CLAUSE_LABELS_BY_FIELD } from '../../objects/clause-sections';
import { ViewEntity } from '../../objects/view-entity';
import { EntityType } from '../../objects/interface-library';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { DocumentViewerSidenavComponent } from '../../entity-form-components/document-viewer-sidenav/document-viewer-sidenav.component';
import { EntityActionsSidenavComponent, EntityAction, EntityTab } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { BaaConnectionsComponent } from '../../entity-form-components/baa-connections/baa-connections.component';
import { CoiConnectionsComponent } from '../../entity-form-components/coi-connections/coi-connections.component';
import { BaaDetailInlineComponent } from '../../entity-form-components/baa-detail-inline/baa-detail-inline.component';
import { CoiDetailInlineComponent } from '../../entity-form-components/coi-detail-inline/coi-detail-inline.component';
import { VendorConnectionsComponent } from '../../entity-form-components/vendor-connections/vendor-connections.component';
import { ContractConnectionsComponent } from '../../entity-form-components/contract-connections/contract-connections.component';
import { AttachmentConnectionsComponent } from '../../entity-form-components/attachment-connections/attachment-connections.component';
import { CriticalDateConnectionsComponent } from '../../entity-form-components/critical-date-connections/critical-date-connections.component';
import { ContractPlanPanelComponent } from '../../shared/components/contract-plan-panel/contract-plan-panel.component';
import { TextContentDialogComponent } from '../../shared/components/text-content-dialog/text-content-dialog.component';
import { ConfirmDialogComponent } from '../../shared/components/confirm-dialog/confirm-dialog.component';
import { SignInEmailDialogComponent, SignInEmailDialogData } from '../../shared/components/sign-in-email-dialog/sign-in-email-dialog.component';
import { AIAnalysisDialogComponent } from '../../shared/components/ai-analysis-dialog/ai-analysis-dialog.component';
import { NewCriticalDateComponent } from '../../create-new/new-critical-date/new-critical-date.component';
import { NewAttachmentComponent } from '../../create-new/new-attachment/new-attachment.component';
import { PeopleConnectionsComponent } from '../../entity-form-components/people-connections/people-connections.component';
import { NotesComponent } from '../../entity-form-components/notes/notes.component';
import { VersionsComponent } from '../../entity-form-components/versions/versions.component';
import { AssessmentConnectionsComponent } from '../../entity-form-components/assessment-connections/assessment-connections.component';
// Hidden - Workflow not ready yet. Uncomment when workflow API is hooked up.
// import { WorkflowConnectionsComponent } from '../../entity-form-components/workflow-connections/workflow-connections.component';
import { NoteRecord } from '../../objects/note-record';
import { DatabaseAction } from '../../objects/interface-library';
import { TimesheetsService } from '../../service/entity-services/timesheets.service';
import { Timesheet } from '../../objects/timesheet';
import { environment } from '../../../environments/environment';
import { normalizeEmail } from '../../shared/utils/normalize-email';

@Component({
  selector: 'app-contract-form',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule,
    MatTabsModule,
    MatExpansionModule,
    MatSidenavModule,
    MatToolbarModule,
    MatTableModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatSnackBarModule,
    DocumentViewerSidenavComponent,
    EntityActionsSidenavComponent,
    BaaConnectionsComponent,
    CoiConnectionsComponent,
    BaaDetailInlineComponent,
    CoiDetailInlineComponent,
    VendorConnectionsComponent,
    ContractConnectionsComponent,
    AttachmentConnectionsComponent,
    CriticalDateConnectionsComponent,
    PeopleConnectionsComponent,
    NotesComponent,
    VersionsComponent,
    AssessmentConnectionsComponent,
    ContractPlanPanelComponent
    // WorkflowConnectionsComponent  // Hidden - Workflow not ready yet
  ],
  templateUrl: './contract-form.component.html',
  styleUrls: ['./contract-form.component.css']
})
export class ContractFormComponent extends ViewEntity implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private sidenavContentService = inject(SidenavContentService);
  public usersService = inject(UsersService);
  private documentAnalysisService = inject(DocumentAnalysisService);
  private cdr = inject(ChangeDetectorRef);
  private timesheetsService = inject(TimesheetsService);
  // partyService is inherited from the ViewEntity base class.
  private destroy$ = new Subject<void>();

  @ViewChild('actionsSidenav') actionsSidenav!: MatSidenav;

  // Environment configuration
  environment = environment;

  /**
   * SANDBOX-ONLY panel: what the nightly contract job will do to this contract.
   * Same gate as the Important Date reminder test panel — only
   * environment.sandbox-ng.ts sets reminderTestTools.
   */
  readonly showContractPlan = (environment as any).reminderTestTools === true;

  currentContract: Contract | null = null;
  loading = true;
  error: string | null = null;

  // AI Analysis state
  aiAnalysisLoading = false;
  aiAnalysisStatusMessage = 'Analyzing contract...';

  // Timesheets
  timesheetCreating = false;
  timesheetAiStatus = signal<string>('');  // '', 'analyzing', 'done', 'failed'
  timesheetAiSuggestions: { field: string; label: string; value: any; sourceText: string }[] = [];
  timesheetAiFieldCount = 0;
  linkedTimesheetSpec: Timesheet | null = null;

  // Display-friendly values
  directVendorName: string = '';
  parentVendorName: string = '';
  legalEntitiesDisplay: string = '';
  sitesDisplay: string = '';
  otherPartiesDisplay: string = '';
  subcontractorsDisplay: string = '';
  primaryOwnerName: string = '';
  secondaryOwnerName: string = '';
  tertiaryOwnerName: string = '';
  internalSignatoryName: string = '';
  contractCoordinatorName: string = '';
  partiesSignatoryName: string = '';
  partyCoordinatorName: string = '';

  // Entity URLs for clickable links
  directVendorUrl: string | null = null;
  parentVendorUrl: string | null = null;
  otherPartiesUrls: Array<{ name: string; url: string }> = [];
  subcontractorsUrls: Array<{ name: string; url: string }> = [];
  masterContractUrl: string | null = null;
  associatedContractsDisplay: Array<{ name: string; contractId: string; url: string | null }> = [];

  // Tab management
  selectedTabIndex = 0;

  // Sidenav actions using signals (Angular 20 pattern)
  sidenavActions = signal<EntityAction[]>([
    {
      label: 'Edit',
      icon: 'edit',
      action: () => this.editContract(),
      disabled: false
    },
    {
      label: 'Add Attachment',
      icon: 'attach_file',
      action: () => this.addAttachment(),
      disabled: false
    },
    {
      label: 'New Important Date',
      icon: 'event',
      action: () => this.addCriticalDate(),
      disabled: false
    },
    {
      label: 'Replace Contract',
      icon: 'swap_horiz',
      action: () => this.replaceContract(),
      disabled: false
    }
  ]);

  // Tabs configuration using signals
  // Feature-flagged tabs are conditionally included based on environment settings
  sidenavTabs = signal<EntityTab[]>(this.buildSidenavTabs());

  private buildSidenavTabs(): EntityTab[] {
    const tabs: EntityTab[] = [
      { label: 'Details', index: 0, icon: 'info' },
      { label: 'Associated Contracts', index: 1, icon: 'link' },
      { label: 'BAAs', index: 2, icon: 'policy' },
      { label: 'COIs', index: 3, icon: 'verified_user' },
      { label: 'Attachments', index: 4, icon: 'attach_file' },
      { label: 'Dates', index: 5, icon: 'event' },
      { label: 'Contract History', index: 6, icon: 'history' },
      { label: 'Provisions', index: 7, icon: 'gavel' },
      { label: 'Notes', index: 8, icon: 'note' },
      { label: 'HCP Benefits', index: 9, icon: 'local_hospital' },
      { label: 'Data Tables', index: 10, icon: 'table_chart' },
      { label: 'Assessments', index: 11, icon: 'assessment' },
      // { label: 'Workflow', index: 12, icon: 'account_tree' },  // Hidden - Workflow not ready yet
    ];

    // Timesheets is org-gated: an org with `timesheets: false` has no timesheets
    // backend wired up, so the tab must not exist (and loadLinkedTimesheet must
    // not fire — see below).
    if (environment.timesheets) {
      tabs.push({ label: 'Enable Timesheets', index: tabs.length, icon: 'schedule' });
    }

    if (environment.grants) {
      tabs.push({ label: 'Grant Information', index: tabs.length, icon: 'account_balance' });
    }
    if (environment.value) {
      tabs.push({ label: 'Value Calculations', index: tabs.length, icon: 'calculate' });
    }
    if (environment.sochsAccordion) {
      tabs.push({ label: 'SOCHS/AP Compensation', index: tabs.length, icon: 'payments' });
    }
    if (environment.hrAccordion) {
      tabs.push({ label: 'HR Details', index: tabs.length, icon: 'work' });
    }
    if (environment.leaseManagement) {
      tabs.push({ label: 'Lease Management', index: tabs.length, icon: 'home_work' });
    }

    return tabs;
  }

  ngOnInit() {
    const contractId = this.route.snapshot.paramMap.get('id');

    if (!contractId) {
      this.error = 'No contract ID provided';
      this.loading = false;
      return;
    }

    this.loadContractFromCache(contractId);

    // Register this component's sidenav toggle with the service
    // This allows the hamburger menu to control our local sidenav
    this.sidenavContentService.registerDetailSidenavToggle(() => {
      if (this.actionsSidenav) {
        this.actionsSidenav.toggle();
      }
    });
  }

  ngOnDestroy() {
    // Unregister our sidenav toggle so the hamburger goes back to controlling the main sidenav
    this.sidenavContentService.unregisterDetailSidenavToggle();

    this.destroy$.next();
    this.destroy$.complete();
  }

  loadContractFromCache(contractNum: string) {
    this.loading = true;
    this.error = null;

    // Fetch contract directly from API by contractNum (partition key)
    this.contractService.getContractByNumber(contractNum)
      .pipe(
        // If not found by contractNum, try by contractId (business ID)
        // Critical dates and other links may reference contractId instead of contractNum
        switchMap(contract => {
          if (contract) return of(contract);
          return this.contractService.getContractById(contractNum);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (contract) => {
          if (contract) {
            this.currentContract = contract;

            // console.log('[DEBUG] Contract Object:', contract);
            // console.log('[DEBUG] Contract criticalDates array:', contract.criticalDates);

            this.processContractData();
            this.loadLinkedTimesheet();
            this.loading = false;
            this.cdr.detectChanges();
          } else {
            this.error = 'Contract not found';
            this.loading = false;
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          console.error('Error loading contract:', err);
          this.error = err.message || 'Failed to load contract. Please try again.';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  processContractData() {
    if (!this.currentContract) return;

    // Subscribe to parties list to look up party names from IDs
    this.allParties$.pipe(takeUntil(this.destroy$)).subscribe(parties => {
      if (!this.currentContract) return;

      // Resolve a party reference to its friendly name, whatever shape it arrives in.
      //
      // These references are genuinely polymorphic on a loaded contract:
      //   - a bare vendorNum string (the shape actually STORED in DynamoDB)
      //   - a FLAT party object (what RehydrationService produces, since
      //     PartyService flattens vendorData out of the cache)
      //   - a WRAPPED party object { vendorNum, vendorData: { name } } (the shape the
      //     backend hydrates into contracts — see CLAUDE.md "Party/Vendor Data
      //     Structure"), whose name is NOT at the root
      //
      // The wrapped case is why names went missing: the old check was
      // `typeof value === 'object' && value.name`, which is false for a wrapped
      // party, so it fell through every branch and returned '' — the field rendered
      // blank / 'N/A' rather than the party's name.
      const lookupPartyName = (value: any): string => {
        if (!value) return '';
        if (typeof value === 'string') {
          // Falls back to the raw id rather than blank: the parties cache streams in
          // pages, so an early lookup can miss. This subscriber re-runs on every
          // emission, so the id is replaced by the name once the party lands.
          const party = parties.find(p => p.vendorNum === value);
          return party?.name || value;
        }
        if (typeof value === 'object') {
          if (value.name) return value.name;                    // flat
          if (value.vendorData?.name) return value.vendorData.name;  // wrapped
          // Object with neither — resolve via its id if it carries one.
          const id = value.vendorNum || value.vendorData?.vendorNum;
          if (id) {
            const party = parties.find(p => p.vendorNum === id);
            return party?.name || id;
          }
        }
        return '';
      };

      // Helper to get party ID from ID or object — same shapes as above.
      const getPartyId = (value: any): string | null => {
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (typeof value === 'object') {
          return value.vendorNum || value.vendorData?.vendorNum || null;
        }
        return null;
      };

      // Direct Vendor
      this.directVendorName = lookupPartyName(this.currentContract.directVendor);
      const directVendorId = getPartyId(this.currentContract.directVendor);
      this.directVendorUrl = this.buildEntityUrl('party', directVendorId);

      // Parent Party
      this.parentVendorName = lookupPartyName(this.currentContract.parentParty);
      const parentVendorId = getPartyId(this.currentContract.parentParty);
      this.parentVendorUrl = this.buildEntityUrl('party', parentVendorId);

      // Other Parties array
      if (Array.isArray(this.currentContract.otherParties) &&
          this.currentContract.otherParties.length > 0) {
        this.otherPartiesDisplay = this.currentContract.otherParties
          .map(party => lookupPartyName(party))
          .join(', ');

        this.otherPartiesUrls = this.currentContract.otherParties
          .map(party => ({
            name: lookupPartyName(party),
            url: this.buildEntityUrl('party', getPartyId(party)) || ''
          }))
          .filter(item => item.url);
      }

      // Subcontractors array
      const subcontractors = this.currentContract.subcontractorVendorNums;

      if (Array.isArray(subcontractors) && subcontractors.length > 0) {
        this.subcontractorsDisplay = subcontractors
          .map(vendorId => lookupPartyName(vendorId))
          .join(', ');

        this.subcontractorsUrls = subcontractors
          .map(vendorId => ({
            name: lookupPartyName(vendorId),
            url: this.buildEntityUrl('party', vendorId) || ''
          }))
          .filter(item => item.url);
      }
    });

    // Subscribe to people list to look up person names from IDs
    this.allPeople$.pipe(takeUntil(this.destroy$)).subscribe(people => {
      if (!this.currentContract) return;

      // Helper to look up person name from ID or object
      const lookupPersonName = (value: any): string => {
        if (!value) return '';
        if (typeof value === 'object') {
          if (value.lastName && value.firstName) return `${value.lastName}, ${value.firstName}`;
          if (value.fullName) return value.fullName;
          if (value.friendlyName) return value.friendlyName;
        }
        if (typeof value === 'string') {
          const person = people.find(p => p.contactNum === value);
          if (person) {
            if (person.lastName && person.firstName) return `${person.lastName}, ${person.firstName}`;
            if (person.fullName) return person.fullName;
          }
          return value; // Fallback to ID
        }
        return '';
      };

      // Party Signatory (person)
      this.partiesSignatoryName = lookupPersonName(this.currentContract.partiesSignatory);

      // Party Contract Coordinator (person)
      this.partyCoordinatorName = lookupPersonName(this.currentContract.partyContractCoordinator);
    });

    // Handle legal entities array - look up names from service
    this.allLegalEntities$.pipe(takeUntil(this.destroy$)).subscribe(legalEntities => {
      if (!this.currentContract) return;

      const contractLEs = this.currentContract.legalEntities as any[];

      if (Array.isArray(contractLEs) && contractLEs.length > 0) {
        this.legalEntitiesDisplay = contractLEs
          .map((le: any) => {
            if (typeof le === 'object' && le.legalEntityName) return le.legalEntityName;
            if (typeof le === 'string') {
              const found = legalEntities.find(e => e.legalEntityNum === le);
              return found?.legalEntityName || le;
            }
            return '';
          })
          .join(', ');
      }
    });

    // Handle sites array - look up names from service
    this.allSites$.pipe(takeUntil(this.destroy$)).subscribe(sites => {
      if (!this.currentContract) return;

      const contractSites = this.currentContract.sites as any[];

      if (Array.isArray(contractSites) && contractSites.length > 0) {
        this.sitesDisplay = contractSites
          .map(site => {
            if (typeof site === 'object' && site.name) return site.name;
            if (typeof site === 'string') {
              const found = sites.find(s => s.siteNum === site);
              return found?.name || site;
            }
            return '';
          })
          .join(', ');
      }
    });

    // Handle master contract URL
    if (this.currentContract.masterContract) {
      const masterContractId = this.extractEntityId(this.currentContract.masterContract, 'contractId');
      // If we got a contractId, we need to look up the contractNum for the URL
      // For now, if masterContract is already a contractNum string, use it directly
      if (typeof this.currentContract.masterContract === 'string') {
        this.masterContractUrl = this.buildEntityUrl('contract', this.currentContract.masterContract);
      } else if (masterContractId) {
        // If it's an object, try to get contractNum from it
        const contractNum = (this.currentContract.masterContract as any).contractNum || masterContractId;
        this.masterContractUrl = this.buildEntityUrl('contract', contractNum);
      }
    }

    // Handle associated contracts (contracts sharing the same primary party)
    const hasAssociated = this.currentContract.associatedContracts && Array.isArray(this.currentContract.associatedContracts) && this.currentContract.associatedContracts.length > 0;

    this.associatedContractsDisplay = [];
    if (hasAssociated) {
      // Look up each associated contract from the full contracts cache (not visibleContracts$ which is filtered)
      this.contractService.allContracts$.pipe(take(1)).subscribe(allContracts => {
        const associatedIds = this.currentContract?.associatedContracts || [];

        this.associatedContractsDisplay = associatedIds
          .map(contractId => {
            const contract = allContracts.find(c => c.contractId === contractId);
            if (contract) {
              // Use nameOfContract, fallback to contractLabel, then contractId
              const contractName = contract.nameOfContract || contract.contractLabel || contractId;
              return {
                name: contractName,
                contractId: contractId,
                url: this.buildEntityUrl('contract', contract.contractNum || contractId)
              };
            }
            // Contract not found in cache - skip it (stale/phantom ID)
            return null;
          })
          .filter((c): c is { name: string; contractId: string; url: string | null } => c !== null);

        this.cdr.detectChanges();
      });
    }

    // Handle users/people - use new extractOwnerDisplayName for hydrated user objects
    // Backend now auto-hydrates: primaryOwner, secondaryOwner, tertiaryOwner, yourContractCoordinator
    this.primaryOwnerName = this.extractOwnerDisplayName(
      (this.currentContract as any).primaryOwner || this.currentContract.primaryResponsibleParty
    );

    this.secondaryOwnerName = this.extractOwnerDisplayName(
      (this.currentContract as any).secondaryOwner
    );

    this.tertiaryOwnerName = this.extractOwnerDisplayName(
      (this.currentContract as any).tertiaryOwner
    );

    this.internalSignatoryName = this.extractOwnerDisplayName(
      this.currentContract.internalSignatory
    );

    this.contractCoordinatorName = this.extractOwnerDisplayName(
      this.currentContract.yourContractCoordinator
    );

    // Note: partiesSignatoryName and partyCoordinatorName are set in the allPeople$ subscription above

    // Load document using base class method
    // Priority: contractFileName/contractFile (bare filename → prepend S3 location)
    // Fallback: linkToContract if it's a valid S3 URL (not an old app URL from ETL)
    const docFileName = this.currentContract.contractFileName || this.currentContract.contractFile;
    const linkToContract = this.currentContract.linkToContract;
    const hasValidS3Link = linkToContract && typeof linkToContract === 'string'
      && linkToContract.includes('.s3.') && linkToContract.includes('amazonaws.com');

    console.log('[ContractForm] Document fields:',
      { contractFileName: this.currentContract.contractFileName,
        contractFile: this.currentContract.contractFile,
        linkToContract,
        hasValidS3Link,
        resolved: docFileName ? environment.s3Location + docFileName : hasValidS3Link ? linkToContract : '(none)' });

    if (docFileName) {
      this.loadDocument(environment.s3Location + docFileName);
    } else if (hasValidS3Link) {
      this.loadDocument(linkToContract);
    }

    // Load critical dates associated with this contract
    // NEW API (Phase 1): Query by parentEntityId, with fallback to legacy criticalDates array
    if (this.currentContract.contractId) {
      console.log('[CriticalDates] Loading for contractId:', this.currentContract.contractId);
      console.log('[CriticalDates] Legacy criticalDates array:', this.currentContract.criticalDates);
      this.criticalDatesService.getDatesByEntityId(this.currentContract.contractId)
        .subscribe(dates => {
          console.log('[CriticalDates] by-entity result:', dates?.length, dates);
          if (dates && dates.length > 0) {
            this.entityDates = dates;
          } else if (this.currentContract?.criticalDates?.length) {
            // Fallback: ETL'd contracts may not have parentEntityId on critical dates
            console.log('[CriticalDates] Falling back to getDatesByNumbers:', this.currentContract.criticalDates);
            this.criticalDatesService.getDatesByNumbers(this.currentContract.criticalDates)
              .subscribe(legacyDates => {
                console.log('[CriticalDates] batch result:', legacyDates?.length, legacyDates);
                this.entityDates = legacyDates || [];
                this.cdr.detectChanges();
              });
          } else {
            this.entityDates = [];
          }
          this.cdr.detectChanges();
        });
    } else {
      this.entityDates = [];
    }

    // Load attachments associated with this contract
    // NEW API (Phase 1): Query by parentEntityId, with fallback to legacy attachment IDs array
    if (this.currentContract.contractId) {
      console.log('[Attachments] Loading for contractId:', this.currentContract.contractId);
      console.log('[Attachments] Legacy attachments array:', this.currentContract.attachments);
      this.attachmentsService.getAttachmentsForEntity(this.currentContract.contractId, 'Contract')
        .subscribe(attachments => {
          console.log('[Attachments] by-entity result:', attachments?.length, attachments);
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentContract?.attachments?.length) {
            // Fallback: ETL'd contracts may not have parentEntityId on attachments
            console.log('[Attachments] Falling back to getAttachmentsByNumbers:', this.currentContract.attachments);
            this.attachmentsService.getAttachmentsByNumbers(this.currentContract.attachments)
              .subscribe(legacyAttachments => {
                console.log('[Attachments] batch result:', legacyAttachments?.length, legacyAttachments);
                this.entityAttachments = legacyAttachments || [];
                this.cdr.detectChanges();
              });
          } else {
            console.log('[Attachments] No attachments array on contract, giving up');
            this.entityAttachments = [];
          }
          this.cdr.detectChanges();
        });
    } else {
      this.entityAttachments = [];
    }
  }

  private extractDisplayName(
    value: any,
    primaryKey: string,
    fallbackKey?: string
  ): string {
    if (!value) return 'N/A';

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object') {
      // Check for nested structure (e.g., legalEntityData, siteData)
      // For legal entities: {legalEntityData: {...}, legalEntityNum: "..."}
      if (value.legalEntityData && value.legalEntityData.legalEntityName) {
        return value.legalEntityData.legalEntityName;
      }

      // For sites: {siteData: {...}, siteNum: "..."}
      if (value.siteData && value.siteData.siteName) {
        return value.siteData.siteName;
      }

      // Direct property access (flat structure)
      if (value[primaryKey]) return value[primaryKey];

      // Common fallback for sites (siteName is the actual property name)
      if (primaryKey === 'name' && value.siteName) return value.siteName;

      if (fallbackKey && value[fallbackKey]) return value[fallbackKey];
    }

    return 'N/A';
  }

  /**
   * Extract display name from owner field (could be hydrated object or email string)
   * If backend returns hydrated object: { userId: "email", userData: { email, given_name, family_name, ... } }
   * If backend returns email string: Look up user in UsersService cache
   * Display format: "Last Name, First Name"
   */
  private extractOwnerDisplayName(owner: any): string {
    if (!owner) return 'Not Assigned';

    // Handle hydrated user object (new format from backend)
    if (typeof owner === 'object' && owner.userData) {
      const { given_name, family_name, email } = owner.userData;
      if (given_name && family_name) {
        return `${family_name}, ${given_name}`;
      }
      return email || owner.userId || 'Unknown User';
    }

    // Handle legacy string format (email) - look up user name from service
    if (typeof owner === 'string') {
      return this.usersService.getUserDisplayNameByEmail(owner);
    }

    // Handle old object format with friendlyName (should not happen with new backend)
    if (typeof owner === 'object' && owner.friendlyName) {
      return owner.friendlyName;
    }

    if (typeof owner === 'object' && owner.email) {
      return owner.email;
    }

    return 'Unknown User';
  }

  /**
   * Extract display name for Person objects (Party Signatory, Party Coordinator)
   * Display format: "Last Name, First Name"
   */
  private extractPersonDisplayName(person: any): string {
    if (!person) return 'Not Assigned';

    // Handle person object with firstName and lastName
    if (typeof person === 'object') {
      const { firstName, lastName, fullName, friendlyName, contactNum } = person;

      if (lastName && firstName) {
        return `${lastName}, ${firstName}`;
      }

      // Fallback to other available name fields
      if (fullName) return fullName;
      if (friendlyName) return friendlyName;
      if (contactNum) return contactNum;
    }

    // Handle string (contactNum or other identifier)
    if (typeof person === 'string') {
      return person;
    }

    return 'Unknown Person';
  }

  editContract() {
    if (this.currentContract?.contractNum) {
      this.router.navigate(['/securehome/new-contract', this.currentContract.contractNum]);
    }
  }

  replaceContract() {
    if (!this.currentContract) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '480px',
      data: {
        title: 'Replace Contract',
        message: 'The current version of this contract will be frozen and moved to Contract History. ' +
                 'You will create a new version that replaces it. The Contract ID will remain the same.\n\n' +
                 'If this is not what you intended, press Cancel now.',
        confirmLabel: 'Continue with Replace',
        cancelLabel: 'Cancel',
        icon: 'swap_horiz',
        warn: true
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed && this.currentContract) {
        this.router.navigate(['/securehome/new-contract'], {
          state: {
            replaceMode: true,
            sourceContract: this.currentContract
          }
        });
      }
    });
  }

  goBack() {
    this.router.navigate(['/securehome/contracts']);
  }

  formatCurrency(value: string | undefined): string {
    if (!value) return 'N/A';
    const num = parseFloat(value);
    return isNaN(num) ? value : `$${num.toLocaleString()}`;
  }

  formatDate(date: string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  }

  // Tab selection handler
  selectTab(index: number): void {
    this.selectedTabIndex = index;
  }

  // Key Contract Sections (Provisions) labels for display
  // Read-only Provisions tab keys off the persisted contract `*Provision` fields.
  // Sourced from the shared CLAUSE_SECTIONS registry so it can't drift out of sync
  // with the intake form / AI dialog (which was the "missing clauses" bug).
  keySectionLabels: { [key: string]: string } = CLAUSE_LABELS_BY_FIELD;

  /**
   * Get key contract sections as array for template iteration
   */
  getKeySectionsArray(): { key: string; label: string; content: string | null }[] {
    if (!this.currentContract) return [];

    const sections: { key: string; label: string; content: string | null }[] = [];

    for (const [key, label] of Object.entries(this.keySectionLabels)) {
      const content = (this.currentContract as any)[key] || null;
      sections.push({ key, label, content });
    }

    return sections;
  }

  /**
   * Check if any key sections have content
   */
  hasAnyKeySections(): boolean {
    if (!this.currentContract) return false;

    for (const key of Object.keys(this.keySectionLabels)) {
      if ((this.currentContract as any)[key]) {
        return true;
      }
    }
    return false;
  }

  // Action methods - using ViewEntity base class
  addAttachment(): void {
    // Prepare entity object with entityType for the dialog
    const entityObjectForDialog = {
      ...this.currentContract,
      entityType: EntityType.Contract
    };

    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityObject: entityObjectForDialog
      }
    });

    dialogRef.afterClosed().subscribe(newAttachment => {
      if (!this.currentContract) return;

      if (newAttachment && newAttachment.attachmentNum) {
        // NEW API (Phase 1): Attachment was created with parentEntityId
        // No need to update contract.attachments array - just refresh from service
        if (this.currentContract!.contractId) {
          this.attachmentsService.getAttachmentsForEntity(this.currentContract!.contractId, 'Contract')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (attachments) => {
                this.entityAttachments = attachments;
                this.cdr.detectChanges();
              },
              error: (err) => {
                console.error('Failed to fetch attachments:', err);
                // Fallback: add the new attachment directly to the list
                this.entityAttachments = [...this.entityAttachments, newAttachment];
                this.cdr.detectChanges();
              }
            });
        }
      }
    });
  }

  addCriticalDate(): void {
    // Prepare entity object with entityType for the dialog
    const entityObjectForDialog = {
      ...this.currentContract,
      entityType: 'Contract'
    };

    const dialogRef = this.dialog.open(NewCriticalDateComponent, {
      width: '800px',
      disableClose: false,
      maxWidth: '90vw',
      maxHeight: '90vh',
      autoFocus: false,
      data: {
        entityObject: entityObjectForDialog
      }
    });

    dialogRef.afterClosed().subscribe(newDate => {
      if (!this.currentContract) {
        console.error('Contract object is not available.');
        return;
      }

      if (newDate && newDate.criticalDateNum) {
        // NEW API (Phase 1): Critical date was created with parentEntityId
        // No need to update contract.criticalDates array - just refresh from service
        if (this.currentContract!.contractId) {
          this.criticalDatesService.getDatesByEntityId(this.currentContract!.contractId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (dates) => {
                this.entityDates = dates;
                this.cdr.detectChanges();
              },
              error: (err) => {
                console.error('Failed to fetch critical dates:', err);
                // Fallback: add the new date directly to the list
                this.entityDates = [...this.entityDates, newDate];
                this.cdr.detectChanges();
              }
            });
        }
      }
    });
  }

  openDescriptionDialog(): void {
    if (!this.currentContract?.description) return;

    this.dialog.open(TextContentDialogComponent, {
      width: '600px',
      data: {
        title: 'Contract Description',
        content: this.currentContract.description
      }
    });
  }

  openTermProvisionDialog(): void {
    if (!this.currentContract?.termProvision) return;

    this.dialog.open(TextContentDialogComponent, {
      width: '600px',
      data: {
        title: 'Initial/Amended Term Provision',
        content: this.currentContract.termProvision
      }
    });
  }

  refreshAttachments(): void {
    // NEW API (Phase 1): Query by parentEntityId instead of array
    if (this.currentContract?.contractId) {
      this.attachmentsService.getAttachmentsForEntity(this.currentContract.contractId, 'Contract')
        .pipe(takeUntil(this.destroy$))
        .subscribe(attachments => {
          this.entityAttachments = attachments;
          this.cdr.detectChanges();
        });
    }
  }

  refreshCriticalDates(): void {
    // NEW API (Phase 1): Query by parentEntityId instead of array
    if (this.currentContract?.contractId) {
      this.criticalDatesService.getDatesByEntityId(this.currentContract.contractId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(dates => {
          this.entityDates = dates;
          this.cdr.detectChanges();
        });
    }
  }

  /**
   * Check if the current document is a PDF (AI analysis only works with PDFs)
   */
  isPdfDocument(): boolean {
    return this.currentDocument?.fileType === 'pdf';
  }

  openAIAnalysis(force: boolean = false): void {
    if (!this.currentContract?.contractId) {
      console.error('[AI Analysis] No contract ID available');
      return;
    }

    // Set loading state
    this.aiAnalysisLoading = true;
    this.aiAnalysisStatusMessage = force ? 'Re-analyzing contract...' : 'Analyzing contract...';
    this.cdr.detectChanges();

    // Progress callback to update status message during polling
    const progressCallback = (progress: AnalysisProgress) => {
      this.aiAnalysisStatusMessage = progress.message;
      this.cdr.detectChanges();
    };

    // Call the AI analysis API with polling (force = bypass cache to pick up new keySections)
    this.documentAnalysisService.triggerAnalysis(this.currentContract.contractId, progressCallback, force)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: DocumentAnalysisResponse) => {
          this.aiAnalysisLoading = false;
          this.cdr.detectChanges();

          // console.log('[AI Analysis] Response received:', response);

          // Handle failed or error status from backend - show simple error dialog
          if (response.status === 'failed' || response.status === 'error') {
            this.dialog.open(AIAnalysisDialogComponent, {
              width: '95vw',
              maxWidth: '1600px',
              height: '90vh',
              panelClass: 'ai-analysis-dialog-panel',
              data: {
                contractName: this.currentContract?.nameOfContract || 'Contract',
                contractId: this.currentContract?.contractId || '',
                contractType: this.currentContract?.contractType,
                contractProvisionAnalysis: this.currentContract?.provisionAnalysis,
                document: this.currentDocument,
                analysisResponse: null,
                // errorMessage is for support/logs and can be raw AWS text.
                analysisError: describeAnalysisFailure(response.errorCode).message
              }
            });
            return;
          }

          // Open the dialog with the analysis results
          this.dialog.open(AIAnalysisDialogComponent, {
            width: '95vw',
            maxWidth: '1600px',
            height: '90vh',
            panelClass: 'ai-analysis-dialog-panel',
            data: {
              contractName: this.currentContract?.nameOfContract || 'Contract',
              contractId: this.currentContract?.contractId || '',
              contractType: this.currentContract?.contractType,
              contractProvisionAnalysis: this.currentContract?.provisionAnalysis,
              document: this.currentDocument,
              analysisResponse: response,
              // "Save to Contract" on the Review tab: show the new clauses on this
              // page's Provisions tab without a reload.
              onClausesSaved: (filled: { [contractField: string]: string }) => {
                if (this.currentContract) {
                  this.currentContract = { ...this.currentContract, ...filled };
                  this.cdr.detectChanges();
                }
              }
            }
          });
        },
        error: (err) => {
          this.aiAnalysisLoading = false;
          this.cdr.detectChanges();

          console.error('[AI Analysis] API call failed:', err);

          // Still open the dialog but without analysis data
          this.dialog.open(AIAnalysisDialogComponent, {
            width: '95vw',
            maxWidth: '1600px',
            height: '90vh',
            panelClass: 'ai-analysis-dialog-panel',
            data: {
              contractName: this.currentContract?.nameOfContract || 'Contract',
              contractId: this.currentContract?.contractId || '',
              contractType: this.currentContract?.contractType,
              contractProvisionAnalysis: this.currentContract?.provisionAnalysis,
              document: this.currentDocument,
              analysisResponse: null,
              analysisError: 'Failed to analyze document. Please try again.'
            }
          });
        }
      });
  }

  saveNotes(notes: NoteRecord[]): void {
    if (!this.currentContract) return;
    this.currentContract.notes = notes;
    this.contractService.writeContract(this.currentContract, 'active', DatabaseAction.Edit)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Failed to save notes:', err)
      });
  }

  // ── Timesheets ─────────────────────────────────────────

  /** Check if this contract already has a linked timesheet spec */
  private loadLinkedTimesheet(): void {
    // Orgs with timesheets disabled have no timesheets backend for this org —
    // the spec list 401s. Don't ask.
    if (!environment.timesheets) return;
    if (!this.currentContract?.contractId) return;

    this.timesheetsService.getAllTimesheetSpecs().pipe(take(1)).subscribe(specs => {
      const found = specs.find(
        (s: Timesheet) => s.contractId === this.currentContract?.contractId
      ) || null;
      // Normalize specId → id
      if (found && !found.id && (found as any).specId) {
        found.id = (found as any).specId;
      }
      this.linkedTimesheetSpec = found;
      this.cdr.detectChanges();
    });
  }

  /** Show confirmation dialog and create a timesheet spec for this contract */
  enableTimesheets(): void {
    if (!this.currentContract) return;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Enable Timesheets',
        message: `This will create a timesheet specification for "${this.currentContract.nameOfContract || 'this contract'}". This links the contract to the timesheet system and cannot be undone. Do you want to continue?`,
        confirmLabel: 'Yes, Enable Timesheets',
        cancelLabel: 'Cancel',
        icon: 'schedule',
        warn: false
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed || !this.currentContract) return;

      this.timesheetCreating = true;
      this.cdr.detectChanges();

      // Get current user email first, then create the spec
      const contractName = this.currentContract.nameOfContract || this.currentContract.contractId || 'Untitled';

      this.usersService.currentUserEmail$.pipe(
        filter((e): e is string => !!e),
        take(1),
        switchMap(email => {
          const contract = this.currentContract!;

          // Extract party vendorNum + (best-effort) email from the contract's
          // embedded directVendor (may be wrapped or flat). The embedded snapshot
          // frequently OMITS the email even when the party record has one, so we
          // only use it as a fallback and resolve authoritatively below.
          const directVendor = contract.directVendor;
          let partyNum: string | undefined;
          let embeddedEmail: string | undefined;
          if (directVendor) {
            partyNum = directVendor.vendorNum
              || directVendor.vendorData?.vendorNum
              || (typeof directVendor === 'string' ? directVendor : undefined);
            embeddedEmail = directVendor.email
              || directVendor.vendorData?.email
              || undefined;
          }

          // Resolve the party's email from the PartyService cache (flat
          // party.email), which is the source of truth. Warm the cache, then wait
          // for it to FINISH streaming before the lookup below.
          //
          // This previously read the first non-empty emission — page 1 of ~45 at
          // cbhi scale — so a party on a later page simply wasn't found, and the
          // user got "This party has no email on file" for a party that has one.
          // settledCache$ can't deadlock either: it returns promptly when the
          // cache is genuinely empty and is bounded when it isn't.
          this.partyService.refreshAllPartiesRecords().subscribe();
          return settledCache$(this.partyService.allParties$).pipe(
            switchMap(parties => {
              const party = partyNum
                ? (parties || []).find((p: any) => p.vendorNum === partyNum)
                : undefined;

              // Vendor-flavored spec (reporting_party set, no userObject): the
              // backend requires principalEmail — the HCP's sign-in email, also
              // its proxy-lookup key. Take it from the party; if the party has
              // none (typical for read-only NetSuite imports), ask for it. It is
              // stored on the spec only — the party is never changed.
              const partyEmail = normalizeEmail(party?.email || embeddedEmail || '');
              const principalEmail$ = partyEmail
                ? of(partyEmail)
                : this.dialog.open<SignInEmailDialogComponent, SignInEmailDialogData, string | null>(SignInEmailDialogComponent, {
                    data: { partyName: party?.name || contractName, feature: 'timesheets' },
                    width: '480px'
                  }).afterClosed().pipe(map(e => e || ''));
              return principalEmail$.pipe(switchMap(principalEmail => {
              if (!principalEmail) {
                this.timesheetCreating = false;
                this.snackBar.open(
                  'No sign-in email was entered, so timesheets were not enabled.',
                  'Dismiss',
                  { duration: 6000 }
                );
                this.cdr.detectChanges();
                return EMPTY;
              }

              const newSpec: any = {
                name: `Timesheet - ${contractName}`,
                contractId: contract.contractId,
                specState: 'draft',
                active: true,
                startDate: contract.effectiveDate || undefined,
                endDate: contract.expirationDate || undefined,
                owner: email,
                reporting_party: partyNum || undefined,
                principalEmail,
                legalEntities: contract.legalEntities || [],
                sites: (contract as any).sites || [],
              };
              return this.timesheetsService.writeTimesheetSpec(newSpec as Timesheet);
              }));
            })
          );
        })
      ).pipe(take(1)).subscribe({
        next: (response: any) => {
          const specId = response.specId || response.spec?.specId;

          // Update contract to mark timesheets as enabled
          this.currentContract!.timesheetsEnabled = true;
          this.contractService.writeContract(this.currentContract!, 'active', DatabaseAction.Edit)
            .pipe(take(1))
            .subscribe();

          // Reload the linked timesheet
          this.loadLinkedTimesheet();
          this.timesheetCreating = false;
          this.cdr.detectChanges();

          // Trigger AI analysis to pre-populate the spec
          this.runTimesheetAiAnalysis(specId);
        },
        error: (err) => {
          // Surface the backend's actual message (err.error is the response body)
          // so validation failures are visible without digging into the Network tab.
          const backendMsg = err?.error?.error || err?.error?.message || err?.message || 'Unknown error';
          console.error('Failed to create timesheet spec:', backendMsg, err?.error ?? err);
          this.snackBar.open(`Could not enable timesheets: ${backendMsg}`, 'Dismiss', { duration: 9000 });
          this.timesheetCreating = false;
          this.cdr.detectChanges();
        }
      });
    });
  }

  /** Run AI analysis on the contract PDF to extract timesheet fields */
  private runTimesheetAiAnalysis(specId: string): void {
    if (!this.currentContract?.contractId) return;

    this.timesheetAiStatus.set('analyzing');
    this.cdr.detectChanges();

    this.documentAnalysisService.triggerAnalysis(this.currentContract.contractId).pipe(
      take(1)
    ).subscribe({
      next: (response: DocumentAnalysisResponse) => {
        const tsFields = (response.extraction as any)?.timesheetFields;

        if (!tsFields || Object.keys(tsFields).length === 0) {
          this.timesheetAiStatus.set('done');
          this.snackBar.open('No timesheet fields found in the contract', 'Dismiss', { duration: 4000 });
          this.cdr.detectChanges();
          return;
        }

        // Separate exact matches (auto-fill) from suggestions
        const specUpdate: any = {};
        if (specId) specUpdate.specId = specId;
        this.timesheetAiSuggestions = [];
        let exactCount = 0;

        const fieldLabels: Record<string, string> = {
          primaryHourlyRate: 'Primary Hourly Rate',
          supplementalHourlyRate: 'Supplemental Hourly Rate',
          premiumHourlyRate: 'Premium Hourly Rate',
          primaryActivities: 'Primary Activities',
          supplementalActivities: 'Supplemental Activities',
          specialty: 'Specialty',
          netPaymentTerms: 'Payment Terms (days)',
          maxFixedCompensationAnnual: 'Max Annual Compensation',
          maxHoursYear: 'Max Hours/Year',
          maxHoursPeriod: 'Max Hours/Period',
          frequency: 'Reporting Frequency',
          employeeType: 'Employee Type'
        };

        for (const [field, data] of Object.entries(tsFields) as [string, any][]) {
          if (data.match === 'exact') {
            specUpdate[field] = data.value;
            exactCount++;
          } else if (data.match === 'suggested') {
            this.timesheetAiSuggestions.push({
              field,
              label: fieldLabels[field] || field,
              value: data.value,
              sourceText: data.sourceText
            });
          }
        }

        this.timesheetAiFieldCount = exactCount;

        // Save exact matches to the spec
        if (exactCount > 0) {
          this.timesheetsService.writeTimesheetSpec(specUpdate as Timesheet).pipe(take(1)).subscribe({
            next: () => {
              this.loadLinkedTimesheet();
            }
          });
        }

        this.timesheetAiStatus.set('done');
        const sugCount = this.timesheetAiSuggestions.length;
        const msg = exactCount > 0
          ? `AI populated ${exactCount} field${exactCount > 1 ? 's' : ''}${sugCount > 0 ? ` and has ${sugCount} suggestion${sugCount > 1 ? 's' : ''}` : ''}`
          : sugCount > 0
            ? `AI has ${sugCount} suggestion${sugCount > 1 ? 's' : ''} from the contract`
            : 'AI analysis complete';
        this.snackBar.open(msg, 'Dismiss', { duration: 5000 });
        this.cdr.detectChanges();
      },
      error: (err) => {
        // AI analysis is optional — failure shouldn't block the spec creation
        console.warn('[TimesheetAI] Analysis failed (non-fatal):', err?.status, err?.error?.message || err?.message);
        this.timesheetAiStatus.set('done');  // Move past the "analyzing" state quietly
        const detail = err?.error?.message || err?.error?.error || 'AI analysis unavailable';
        this.snackBar.open(`Spec created. ${detail}`, 'Dismiss', { duration: 4000 });
        this.cdr.detectChanges();
      }
    });
  }

  /** Accept an AI suggestion and apply it to the linked spec */
  acceptAiSuggestion(suggestion: { field: string; value: any }): void {
    if (!this.linkedTimesheetSpec) return;

    const specId = this.linkedTimesheetSpec.id || (this.linkedTimesheetSpec as any).specId;
    const update: any = { specId };
    update[suggestion.field] = suggestion.value;

    this.timesheetsService.writeTimesheetSpec(update as Timesheet).pipe(take(1)).subscribe({
      next: () => {
        // Remove from suggestions list
        this.timesheetAiSuggestions = this.timesheetAiSuggestions.filter(s => s.field !== suggestion.field);
        this.loadLinkedTimesheet();
        this.snackBar.open('Suggestion applied', 'Dismiss', { duration: 2000 });
        this.cdr.detectChanges();
      }
    });
  }

  /** Dismiss an AI suggestion */
  dismissAiSuggestion(field: string): void {
    this.timesheetAiSuggestions = this.timesheetAiSuggestions.filter(s => s.field !== field);
    this.cdr.detectChanges();
  }

  isArray(val: any): boolean { return Array.isArray(val); }

  /** Open the linked timesheet spec in a new tab */
  openTimesheetSpec(): void {
    if (this.linkedTimesheetSpec?.id) {
      window.open(`/securehome/timesheet-spec/${this.linkedTimesheetSpec.id}`, '_blank');
    }
  }

  /** Navigate to the timesheet edit page for the linked spec */
  editTimesheetSpec(): void {
    if (this.linkedTimesheetSpec?.id) {
      window.open(`/securehome/timesheet-edit/${this.linkedTimesheetSpec.id}`, '_blank');
    }
  }
}